import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from 'components/ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table';
import { StatusBadge } from 'components/ui/status-badge';
import { Button } from 'components/ui/button';
import { DataTableToolbar, TableSearch, SegmentedTabs, ColumnsToggle, TableCount, DataTablePagination } from 'components/ui/data-table';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { useTestCategoryLabel } from 'hooks/use-test-category-label';
import { useTests, useItemAnalysis } from 'modules/analytics';
import { toPlainText } from 'modules/editor';
import type { TestPerfRow, ItemStat } from 'modules/analytics';
import type { AnalyticsFilters } from '../useAnalyticsFilters';
import { SectionCard, LoadingBlock, ErrorBlock, EmptyBlock, scoreColorClass, ItemFlagBadge } from './shared';
import { ScoreDistributionBar, ItemDifficultyBar, DiscriminationScatter } from './charts';

const Funnel: React.FC<{ funnel: Array<{ stage: string; count: number }> }> = ({ funnel }) => {
  const { t } = useTranslation();
  const top = funnel[0]?.count || 1;
  return (
    <div className="space-y-3">
      {funnel.map((s) => (
        <div key={s.stage}>
          <div className="mb-1 flex items-center justify-between text-[13px]">
            <span className="text-foreground/80">{t(`analytics_funnel_${s.stage}`)}</span>
            <span className="font-semibold tabular-nums text-foreground">{s.count}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-foreground" style={{ width: `${Math.round((s.count / top) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const ItemAnalysisPanel: React.FC<{ orgId?: string; filters: AnalyticsFilters }> = ({ orgId, filters }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useItemAnalysis({ orgId, testId: filters.testId, params: filters.params });

  if (isError) return <ErrorBlock message={error instanceof Error ? error.message : undefined} />;
  if (isLoading) return <LoadingBlock />;
  if (!data || data.items.length === 0) return <EmptyBlock message={t('analytics_items_empty')} />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title={t('analytics_items_difficulty_chart')} description={t('analytics_items_difficulty_hint')}>
          <ItemDifficultyBar items={data.items} />
        </SectionCard>
        <SectionCard title={t('analytics_items_quality_map')} description={t('analytics_items_quality_hint')}>
          <DiscriminationScatter items={data.items} />
        </SectionCard>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[720px]">
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>{t('analytics_items_question')}</TableHead>
                <TableHead>{t('analytics_items_responses')}</TableHead>
                <TableHead>{t('analytics_items_p_value')}</TableHead>
                <TableHead>{t('analytics_items_point_biserial')}</TableHead>
                <TableHead>{t('analytics_items_d_index')}</TableHead>
                <TableHead>{t('analytics_items_flag')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((it: ItemStat, idx) => (
                <TableRow key={it.questionId}>
                  <TableCell className="text-muted-foreground tabular-nums">{it.order || idx + 1}</TableCell>
                  <TableCell className="max-w-md">
                    <p className="text-sm text-foreground truncate" title={toPlainText(it.prompt)}>
                      {toPlainText(it.prompt) || '—'}
                    </p>
                  </TableCell>
                  <TableCell className="tabular-nums">{it.responses}</TableCell>
                  <TableCell className={`tabular-nums font-semibold ${scoreColorClass(it.pValue * 100)}`}>
                    {Math.round(it.pValue * 100)}%
                  </TableCell>
                  <TableCell className="tabular-nums">{it.discrimination.pointBiserial ?? '—'}</TableCell>
                  <TableCell className="tabular-nums">{it.discrimination.dIndex ?? '—'}</TableCell>
                  <TableCell><ItemFlagBadge flag={it.flag} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const TestsQuestionsTab: React.FC<{ orgId?: string; filters: AnalyticsFilters }> = ({ orgId, filters }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useTests({ orgId, params: filters.params });
  const categoryLabel = useTestCategoryLabel();

  // `tq`, not `q`: the People tab owns `q` (see useAnalyticsFilters).
  const { input: search, setInput: setSearch } = useSearchParamState('tq');
  const [statusTab, setStatusTab] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [cols, setCols] = useState({ category: true, attempts: true, graded: true, avgScore: true, passRate: true, avgTime: true });

  const tests = useMemo(() => data?.tests ?? [], [data]);

  // Client-side status tab + title search, then paginate.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tests.filter((row) => {
      if (statusTab !== 'all' && (row.status?.toLowerCase() || '') !== statusTab) return false;
      if (q && !row.title.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [tests, search, statusTab]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Snap back to a valid page whenever the filtered set changes.
  useEffect(() => {
    setPage(1);
  }, [search, statusTab, pageSize]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard className="lg:col-span-2" title={t('analytics_score_distribution')} description={t('analytics_score_distribution_hint')}>
          {isError ? (
            <ErrorBlock message={error instanceof Error ? error.message : undefined} />
          ) : isLoading ? (
            <LoadingBlock />
          ) : !data || data.distribution.every((b) => b.count === 0) ? (
            <EmptyBlock message={t('analytics_no_graded')} />
          ) : (
            <ScoreDistributionBar data={data.distribution} />
          )}
        </SectionCard>
        <SectionCard title={t('completion_funnel')}>
          {isLoading || !data ? <LoadingBlock /> : <Funnel funnel={data.funnel} />}
        </SectionCard>
      </div>

      {!isLoading && !isError && data && data.tests.length > 0 && (
        <DataTableToolbar>
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder={t('search_by_test_name')}
            aria-label={t('search_by_test_name')}
          />
          <ColumnsToggle
            columns={[
              { id: 'category', label: t('category'), visible: cols.category },
              { id: 'attempts', label: t('attempts'), visible: cols.attempts },
              { id: 'graded', label: t('graded_attempts'), visible: cols.graded },
              { id: 'avgScore', label: t('avg_score'), visible: cols.avgScore },
              { id: 'passRate', label: t('pass_rate'), visible: cols.passRate },
              { id: 'avgTime', label: t('analytics_people_avg_time'), visible: cols.avgTime },
            ]}
            onChange={(id, visible) => setCols((c) => ({ ...c, [id]: visible }))}
          />
          <SegmentedTabs
            aria-label={t('status')}
            value={statusTab}
            onChange={setStatusTab}
            options={[
              { value: 'all', label: t('all') },
              { value: 'active', label: t('active') },
              { value: 'draft', label: t('draft') },
              { value: 'archived', label: t('archived') },
            ]}
          />
          <TableCount>
            {filtered.length} {t('items')}
          </TableCount>
        </DataTableToolbar>
      )}

      {isError ? (
        <ErrorBlock message={error instanceof Error ? error.message : undefined} />
      ) : isLoading ? (
        <LoadingBlock />
      ) : !data || data.tests.length === 0 ? (
        <EmptyBlock message={t('no_tests_yet')} />
      ) : filtered.length === 0 ? (
        <EmptyBlock message={t('no_tests_yet')} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table className="min-w-[800px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('analytics_test')}</TableHead>
                  {cols.category && <TableHead>{t('category')}</TableHead>}
                  {cols.attempts && <TableHead>{t('attempts')}</TableHead>}
                  {cols.graded && <TableHead>{t('graded_attempts')}</TableHead>}
                  {cols.avgScore && <TableHead>{t('avg_score')}</TableHead>}
                  {cols.passRate && <TableHead>{t('pass_rate')}</TableHead>}
                  {cols.avgTime && <TableHead>{t('analytics_people_avg_time')}</TableHead>}
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.map((row: TestPerfRow) => {
                  const selected = filters.testId === row.testId;
                  return (
                    <TableRow
                      key={row.testId}
                      className={`cursor-pointer ${selected ? 'bg-muted' : ''}`}
                      onClick={() => filters.setTestId(selected ? undefined : row.testId)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">{row.title}</p>
                          <StatusBadge tone={row.status?.toLowerCase() === 'active' ? 'success' : 'neutral'}>
                            {row.status}
                          </StatusBadge>
                        </div>
                      </TableCell>
                      {cols.category && (
                        <TableCell className="max-w-[180px] text-sm text-foreground/80">
                          {categoryLabel(row.category) ? (
                            <span className="block truncate" title={categoryLabel(row.category)}>
                              {categoryLabel(row.category)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/60">—</span>
                          )}
                        </TableCell>
                      )}
                      {cols.attempts && <TableCell className="tabular-nums">{row.total}</TableCell>}
                      {cols.graded && <TableCell className="tabular-nums">{row.graded}</TableCell>}
                      {cols.avgScore && (
                        <TableCell className={`tabular-nums font-semibold ${scoreColorClass(row.avgScore)}`}>{row.avgScore}%</TableCell>
                      )}
                      {cols.passRate && <TableCell className="tabular-nums">{row.passRate}%</TableCell>}
                      {cols.avgTime && (
                        <TableCell className="tabular-nums text-muted-foreground">
                          {row.avgDuration == null ? '—' : `${row.avgDuration}m`}
                        </TableCell>
                      )}
                      <TableCell>
                        <ChevronRight size={16} className={`transition-transform ${selected ? 'rotate-90 text-foreground' : 'text-muted-foreground/50'}`} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <DataTablePagination
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      {filters.testId && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-foreground">{t('analytics_items_heading')}</h3>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => filters.setTestId(undefined)}>
              <X className="h-3.5 w-3.5" aria-hidden />
              {t('analytics_items_clear')}
            </Button>
          </div>
          <ItemAnalysisPanel orgId={orgId} filters={filters} />
        </div>
      )}
    </div>
  );
};

export default TestsQuestionsTab;
